import {
  Alert,
  Anchor,
  Box,
  Button,
  Card,
  Group,
  Modal,
  NumberFormatter,
  RingProgress,
  Stack,
  Text,
  Title
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import {
  IconAlertTriangleFilled,
  IconCircleCheckFilled
} from "@tabler/icons-react";
import type { DateTime } from "luxon";
import { generatePath, Link } from "react-router";
import { useFakeProgress } from "../../../hooks/useFakeProgress";
import { ModalFooter } from "../../../mantine/components/ModalFooter/ModalFooter";
import { AlertPresets } from "../../../mantine/mantinePresets";
import { ROUTES } from "../../../routes";
import type { components } from "../../../types/api.types";
import { ContentWithSkeleton } from "../../ContentWithSkeleton/ContentWithSkeleton";
import { TABS as ENERGY_DATA_TABS } from "../EnergyDataView/EnergyDataView";
import { LOCATION_ID_PREFIX } from "../EnergyDataView/utils/getLocationIdFromSelectedId";
import { METER_ID_PREFIX } from "../EnergyDataView/utils/getMeterIdFromSelectedId";

export type DataQualityMeloMaloListItem =
  components["schemas"]["DataQualityMeloMaloListItem"];

interface EnergyDataQualityProps {
  title?: string;
  qualityPercent: number | null;
  totalVolume?: number | null;
  missingDataSources?: Array<DataQualityMeloMaloListItem>;
  selectedDateRange?: [DateTime<true>, DateTime<true> | null];
  isLoading?: boolean;
}

function EnergyDataQuality({
  title,
  qualityPercent,
  totalVolume,
  missingDataSources,
  selectedDateRange,
  isLoading
}: EnergyDataQualityProps) {
  const [isOpen, { close, open }] = useDisclosure(false);

  const fakeProgress = useFakeProgress({
    autoStart: true,
    enabled: isLoading,
    speed: "slow"
  });

  if (isLoading) {
    qualityPercent = fakeProgress.value;
  }

  function getListOfMissingDataSources() {
    if (
      missingDataSources?.length == 0 &&
      (qualityPercent == 0 || totalVolume == 0)
    ) {
      return;
    }
    return missingDataSources?.length && missingDataSources?.length > 0 ? (
      <>
        <Group fw="500" gap="xs">
          <IconAlertTriangleFilled color="var(--mantine-color-warning-filled)" />
          Zählerdaten fehlen
          <Button h={"unset"} p={0} variant="subtle" onClick={open}>
            (ansehen)
          </Button>
        </Group>
        {missingDataSources && (
          <ListOfMissingDataSourcesModal
            close={close}
            missingDataSources={missingDataSources}
            opened={isOpen}
            selectedDateRange={selectedDateRange}
          />
        )}
      </>
    ) : (
      <Group fw="500" gap="xs">
        <IconCircleCheckFilled color="var(--mantine-color-success-filled)" />
        Alle Daten vollständig
      </Group>
    );
  }

  return (
    <Card className="EnergyDataQuality" withBorder>
      <Title fw="600" mb="xs" order={4} size="h5">
        {`Datenverfügbarkeit${title ? `: ${title}` : ""}`}
      </Title>
      {qualityPercent === null ? (
        <Alert
          color={AlertPresets.warning.color}
          icon={AlertPresets.warning.icon}
        >
          Für die getroffene Auswahl stehen aktuell noch keine Einspeisedaten
          zur Verfügung.
        </Alert>
      ) : (
        <Group align="center" gap="xl">
          <RingProgress
            label={
              <Text c="#9050f7" fz="md" lh={1} ta="center">
                {Math.floor(qualityPercent)}&nbsp;%
              </Text>
            }
            rootColor="neutral.3"
            sections={[
              {
                value: qualityPercent,
                color: "#9050f7"
              }
            ]}
            size={100}
            thickness={10}
          />
          <Box>
            <Stack flex={1} gap="sm">
              <Box w="fit-content">
                <Title c="neutral.6" fw="400" mb={0} order={6} size="xs">
                  Menge Einspeisung (kWh)
                </Title>
                <Box fw="500">
                  <ContentWithSkeleton isLoading={isLoading}>
                    {typeof totalVolume === "number" ? (
                      <NumberFormatter
                        decimalScale={2}
                        fixedDecimalScale
                        value={totalVolume}
                      />
                    ) : (
                      "–"
                    )}
                  </ContentWithSkeleton>
                </Box>
              </Box>
              <Box w="fit-content">
                <Title c="neutral.6" fw="400" mb={0} order={6} size="xs">
                  Vollständigkeit
                </Title>
                <ContentWithSkeleton isLoading={isLoading}>
                  {getListOfMissingDataSources() ?? "–"}
                </ContentWithSkeleton>
              </Box>
            </Stack>
          </Box>
        </Group>
      )}
    </Card>
  );
}

interface MissingDataSourceLinkProps {
  dataSource: DataQualityMeloMaloListItem;
  selectedDateRange?: [DateTime<true>, DateTime<true> | null];
}

function MissingDataSourceLink({
  dataSource,
  selectedDateRange
}: MissingDataSourceLinkProps) {
  const label =
    dataSource.site_name +
    " — " +
    (dataSource.meter_name ?? dataSource.melo_malo_name);

  const meterIdForEnergyDataLink = dataSource.meter_id
    ? `${METER_ID_PREFIX}${dataSource.meter_id}`
    : undefined;
  const locationIdForEnergyDataLink = dataSource.melo_malo_id
    ? `${LOCATION_ID_PREFIX}${dataSource.melo_malo_id}`
    : undefined;
  const selectedIdForEnergyDataLink =
    meterIdForEnergyDataLink || locationIdForEnergyDataLink;

  const dateUrlParams = selectedDateRange
    ? `firstDate=${selectedDateRange[0].toISODate()}&lastDate=${selectedDateRange[1]?.toISODate() || ""}&`
    : "";
  const urlParams = `?${dateUrlParams}selectedId=${selectedIdForEnergyDataLink}`;

  return (
    <Text>
      <Anchor
        component={Link}
        to={{
          pathname: generatePath(
            `${ROUTES.energyData.entry}/${ENERGY_DATA_TABS.TAB_RAW_ENERGY_DATA}`,
            { projectId: dataSource.project_id }
          ),
          search: urlParams
        }}
      >
        {label}
      </Anchor>
    </Text>
  );
}

function ListOfMissingDataSourcesModal({
  missingDataSources,
  selectedDateRange,
  opened,
  close
}: {
  missingDataSources: Array<DataQualityMeloMaloListItem>;
  selectedDateRange?: [DateTime<true>, DateTime<true> | null];
  opened: boolean;
  close: () => void;
}) {
  return (
    <Modal opened={opened} title={`Fehlende Zählerdaten`} onClose={close}>
      {missingDataSources.map((src) => (
        <MissingDataSourceLink
          dataSource={src}
          key={src.meter_id || src.melo_malo_id}
          selectedDateRange={selectedDateRange}
        />
      ))}
      <ModalFooter>
        <Button variant="default" onClick={close}>
          Schließen
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export { EnergyDataQuality, EnergyDataQualityProps };
